import type {
  OptimizationProfile,
  OptimizationProfileId,
  VerdictAxis,
  VerdictStatus,
  Verdict,
  ModelProbeReport,
} from './types';

// Three AR quality presets. They differ in texture resolution/quality and the
// geometry visual-error budget; the texture controls remain individually
// editable after picking a preset. Medium is the recommended default — its
// 0.15% error budget is the perceptual knee for dense organic meshes.

export const AR_HIGH: OptimizationProfile = {
  id: 'AR-high',
  label: 'High',
  description: 'Best fidelity. For WiFi / strong data — preserves fine detail.',
  maxFileSizeBytes: 15 * 1024 * 1024, // 15 MB
  maxTextureSideLength: 2048,
  preferJpg: true,
  jpgQuality: 95,
  maxVertexCount: 200_000,
  simplifyRatio: 0.7, // deprecated, ignored by optimize()
  geometryDetail: 0.9, // keep 90% — high fidelity
  quantize: true,
  attention: {
    maxVertexCount: 100_000,
  },
};

export const AR_MEDIUM: OptimizationProfile = {
  id: 'AR-medium',
  label: 'Medium',
  description: 'Recommended default. Balances quality and load time on mobile.',
  maxFileSizeBytes: 10 * 1024 * 1024, // 10 MB
  maxTextureSideLength: 1024,
  preferJpg: true,
  jpgQuality: 70,
  maxVertexCount: 100_000,
  simplifyRatio: 0.35,
  geometryDetail: 0.8, // keep 80% — balanced default
  quantize: true,
  attention: {
    maxVertexCount: 50_000,
  },
};

export const AR_LOW: OptimizationProfile = {
  id: 'AR-low',
  label: 'Low',
  description: 'Smallest files. For slow networks or always-on ambient content.',
  maxFileSizeBytes: 8 * 1024 * 1024, // 8 MB
  maxTextureSideLength: 512,
  preferJpg: true,
  jpgQuality: 70,
  maxVertexCount: 50_000,
  simplifyRatio: 0.1,
  geometryDetail: 0.4, // keep 40% — strong reduction for smallest files
  quantize: true,
  attention: {
    maxVertexCount: 25_000,
  },
};

export const BUILT_IN_PROFILES: readonly OptimizationProfile[] = [
  AR_HIGH,
  AR_MEDIUM,
  AR_LOW,
];

export function getProfileById(id: OptimizationProfileId): OptimizationProfile | null {
  return BUILT_IN_PROFILES.find(p => p.id === id) ?? null;
}

function fmtBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function worst(statuses: VerdictStatus[]): VerdictStatus {
  if (statuses.includes('red')) return 'red';
  if (statuses.includes('yellow')) return 'yellow';
  return 'green';
}

function evalFileSize(report: ModelProbeReport, profile: OptimizationProfile): VerdictAxis {
  const actual = report.fileSize;
  const target = profile.maxFileSizeBytes;
  const actualStr = fmtBytes(actual); // eslint-disable-line
  const targetStr = `≤ ${fmtBytes(target)}`;

  if (actual <= target) {
    return {
      axis: 'fileSize',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'File size will load quickly on mobile networks.',
    };
  }
  if (actual <= target * 1.5) {
    return {
      axis: 'fileSize',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Moderately large for AR delivery. Expect slower first-paint on mobile networks.',
    };
  }
  return {
    axis: 'fileSize',
    status: 'red',
    actual: actualStr,
    target: targetStr,
    explanation: 'Large file — mobile viewers may abandon while waiting. Consider optimization or a lower-fidelity profile.',
  };
}

function evalVertexCount(report: ModelProbeReport, profile: OptimizationProfile): VerdictAxis {
  const actual = report.geometry.vertexCount;
  const target = profile.maxVertexCount;
  const actualStr = actual.toLocaleString();
  const targetStr = `≤ ${target.toLocaleString()}`;

  if (actual <= target) {
    return {
      axis: 'vertexCount',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'Geometry is within the target range.',
    };
  }
  if (actual <= target * 1.3) {
    return {
      axis: 'vertexCount',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Moderately over target. Consider simplification to improve load time.',
    };
  }
  return {
    axis: 'vertexCount',
    status: 'red',
    actual: actualStr,
    target: targetStr,
    explanation: 'Vertex count is well above target. Simplification is strongly recommended.',
  };
}

function evalTextureResolution(report: ModelProbeReport, profile: OptimizationProfile): VerdictAxis | null {
  if (report.textures.textureCount === 0) {
    return null;
  }
  // Use the largest measured texture dimension (the probe decodes real width/
  // height). Fall back to skipping the axis if no dimensions were captured.
  const sides = report.textures.textures
    .flatMap(t => [t.width, t.height])
    .filter((n): n is number => typeof n === 'number' && n > 0);
  if (sides.length === 0) return null;
  const actual = Math.max(...sides);
  const target = profile.maxTextureSideLength;
  const actualStr = `${actual}px`;
  const targetStr = `≤ ${target}px`;

  if (actual <= target) {
    return {
      axis: 'textureResolution',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'Texture resolution is within target.',
    };
  }
  if (actual <= target * 1.5) {
    return {
      axis: 'textureResolution',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'Textures are moderately above target. Resampling recommended to save bandwidth.',
    };
  }
  return {
    axis: 'textureResolution',
    status: 'red',
    actual: actualStr,
    target: targetStr,
    explanation: 'Textures are well above target. Consider downsampling.',
  };
}

function evalTextureFormat(report: ModelProbeReport, profile: OptimizationProfile): VerdictAxis | null {
  if (report.textures.textureCount === 0) return null;
  if (!profile.preferJpg) return null;

  // The healthy strategy is JPEG for color maps (base color, emissive) and PNG
  // for data maps (normal, metallic-roughness, occlusion) — JPEG corrupts the
  // values encoded in data maps. Judge against that, not "all one format".
  const colorIdx = new Set<number>();
  const dataIdx = new Set<number>();
  for (const mat of report.materials.materials ?? []) {
    for (const ref of mat.textures) {
      if (ref.slot === 'baseColor' || ref.slot === 'emissive') colorIdx.add(ref.textureIndex);
      else dataIdx.add(ref.textureIndex);
    }
  }
  const mimeOf = (i: number) => report.textures.textures[i]?.mimeType;

  // A color map "should be JPEG" only if it carries no alpha (alpha needs PNG).
  // Mirror analyzeMaterial's hint test: analyzeAlpha never returns hasAlpha
  // false for a PNG (only true/undefined), so the real "no alpha" signal is a
  // measured opaque usage — using hasAlpha === false alone left this branch dead.
  const colorPngWithoutAlpha = [...colorIdx].filter(i => {
    const t = report.textures.textures[i];
    return t?.mimeType === 'image/png' && (t.alpha?.usage === 'opaque' || t.hasAlpha === false);
  });
  const dataAsJpeg = [...dataIdx].filter(i => mimeOf(i) === 'image/jpeg');

  if (dataAsJpeg.length > 0) {
    return {
      axis: 'textureFormat',
      status: 'yellow',
      actual: 'JPEG data maps',
      target: 'PNG data maps',
      explanation: 'Normal/roughness maps are stored as JPEG, which corrupts the encoded values. Re-export them as PNG.',
    };
  }
  if (colorPngWithoutAlpha.length > 0) {
    return {
      axis: 'textureFormat',
      status: 'yellow',
      actual: 'PNG color maps',
      target: 'JPEG color maps',
      explanation: 'Some color maps are PNG with no alpha. Converting them to JPEG shrinks the file with no visible loss (the optimizer does this).',
    };
  }
  return {
    axis: 'textureFormat',
    status: 'green',
    actual: 'JPEG color · PNG data',
    target: 'JPEG color · PNG data',
    explanation: 'Color maps use JPEG; data maps (normal/roughness) stay PNG — the right balance.',
  };
}

function evalMaterialCount(report: ModelProbeReport, _profile: OptimizationProfile): VerdictAxis | null {
  if (report.materials.count === 0) return null;
  const actual = report.materials.count;
  // Simple heuristic: warn if > 50 materials
  const actualStr = `${actual}`;
  const targetStr = '≤ 50 (guideline)';
  if (actual <= 50) {
    return {
      axis: 'materialCount',
      status: 'green',
      actual: actualStr,
      target: targetStr,
      explanation: 'Material count is reasonable.',
    };
  }
  if (actual <= 100) {
    return {
      axis: 'materialCount',
      status: 'yellow',
      actual: actualStr,
      target: targetStr,
      explanation: 'High material count may impact rendering performance. Consider consolidating.',
    };
  }
  return {
    axis: 'materialCount',
    status: 'yellow',
    actual: actualStr,
    target: targetStr,
    explanation: 'Very high material count. Consolidating materials can improve load and render times.',
  };
}

export function evaluateAgainstProfile(report: ModelProbeReport, profile: OptimizationProfile): Verdict {
  // A bailed probe measured nothing, and every axis scores a zero as comfortably
  // under budget — so a file we couldn't even parse would come back "Ready for
  // AR" on 0 vertices. There is no verdict to give; say that instead.
  if (report.bailout) {
    return {
      overall: 'red',
      axes: [{
        axis: 'analysis',
        status: 'red',
        actual: 'not analyzed',
        target: 'readable glTF/GLB',
        explanation:
          'The file could not be read, so none of the AR budgets could be checked. See the issues above for why.',
      }],
    };
  }

  const axes: VerdictAxis[] = [];

  axes.push(evalFileSize(report, profile));
  axes.push(evalVertexCount(report, profile));

  const resAxis = evalTextureResolution(report, profile);
  if (resAxis) axes.push(resAxis);

  const fmtAxis = evalTextureFormat(report, profile);
  if (fmtAxis) axes.push(fmtAxis);

  const matAxis = evalMaterialCount(report, profile);
  if (matAxis) axes.push(matAxis);

  return {
    overall: worst(axes.map(a => a.status)),
    axes,
  };
}
